(() => {
  if (!document.querySelector('facet-filters-form, #FacetFiltersForm, #FacetFiltersFormMobile, #main-collection-filters, #main-search-filters, #ProductGridContainer, .template-search__results')) return;
  if (window.OCFilterScroll) return;

  const PREFIX = 'oc-filter-scroll-v1';
  const KEY_Y = `${PREFIX}:y`;
  const KEY_SCOPE = `${PREFIX}:scope`;
  const KEY_PENDING = `${PREFIX}:pending`;
  const KEY_PAGE = `${PREFIX}:page`;
  const KEY_BROWSE_URL = `${PREFIX}:browse-url`;
  const KEY_GRID = `${PREFIX}:grid`;
  let browseGrid = null;
  let snapshotApplied = false;

  const storage = (() => {
    try {
      const test = `${PREFIX}:test`;
      sessionStorage.setItem(test, '1');
      sessionStorage.removeItem(test);
      return sessionStorage;
    } catch (_) {
      return null;
    }
  })();

  const scope = () => window.location.pathname;
  const hasStoredPosition = () => storage && storage.getItem(KEY_SCOPE) === scope() && storage.getItem(KEY_Y) !== null;

  const resultsTarget = () =>
    document.querySelector('[data-oc-filter-jump-anchor]') ||
    document.getElementById('ProductGridContainer') ||
    document.querySelector('.template-search__results') ||
    document.getElementById('product-grid');

  const settleAt = (y) => {
    const top = Math.max(0, Math.round(Number(y) || 0));
    window.scrollTo({ top, left: 0, behavior: 'auto' });
    requestAnimationFrame(() => {
      window.scrollTo({ top, left: 0, behavior: 'auto' });
      window.setTimeout(() => window.scrollTo({ top, left: 0, behavior: 'auto' }), 90);
    });
  };

  const scrollToResults = () => {
    const target = resultsTarget();
    if (!target) return;
    const filterBox = document.querySelector('#main-collection-filters, #main-search-filters');
    const stickyTop = filterBox && getComputedStyle(filterBox).position === 'sticky'
      ? parseFloat(getComputedStyle(filterBox).top) || 16
      : 12;
    const alignOffset = filterBox && target.hasAttribute('data-oc-filter-jump-anchor')
      ? parseFloat(filterBox.style.getPropertyValue('--oc-facet-card-align-offset')) || 0
      : 0;
    const y = target.getBoundingClientRect().top + window.scrollY + alignOffset - stickyTop;
    settleAt(y);
  };

  const clearSavedBrowse = () => {
    [KEY_Y, KEY_SCOPE, KEY_PENDING, KEY_PAGE, KEY_BROWSE_URL, KEY_GRID].forEach((key) => storage?.removeItem(key));
    browseGrid = null;
    snapshotApplied = false;
  };

  let restoring = null;
  const savedPage = () => Math.max(1, parseInt(storage?.getItem(KEY_PAGE) || '1', 10) || 1);
  const restoreBrowsePosition = (keepSaved = false) => {
    if (!hasStoredPosition()) return;
    if (restoring) return restoring;
    if (snapshotApplied && !storage.getItem(KEY_BROWSE_URL)) {
      settleAt(Number(storage.getItem(KEY_Y) || 0));
      if (!keepSaved) clearSavedBrowse();
      return;
    }
    restoring = (async () => {
      const browseUrl = storage.getItem(KEY_BROWSE_URL);
      if (browseUrl && browseUrl !== window.location.href && !window.OCFilterScroll.hasFilterParams()) {
        window.location.assign(browseUrl);
        return;
      }

      const page = savedPage();
      if (!snapshotApplied && page > 1 && window.OCLoadMore && !await window.OCLoadMore.restoreToPage(page)) return;

      settleAt(Number(storage.getItem(KEY_Y) || 0));
      if (!keepSaved) clearSavedBrowse();
    })().finally(() => { restoring = null; });
    return restoring;
  };

  window.OCFilterScroll = {
    saveBrowsePosition() {
      if (!storage) return;
      if (!hasStoredPosition()) {
        storage.setItem(KEY_SCOPE, scope());
        storage.setItem(KEY_Y, String(window.scrollY));
        storage.setItem(KEY_PAGE, String(window.OCLoadMore?.getLoadedPage() || 1));
        browseGrid = document.getElementById('ProductGridContainer')?.innerHTML ?? null;
        if (browseGrid !== null) {
          try { storage.setItem(KEY_GRID, browseGrid); }
          catch (_) { storage.removeItem(KEY_GRID); }
        }
        const url = new URL(window.location.href);
        if (url.searchParams.get('oc') === 'paged' && !this.hasFilterParams(url.searchParams)) {
          storage.setItem(KEY_BROWSE_URL, url.href);
        }
      }
    },

    markJumpAfterNavigation() {
      if (!storage) return;
      this.saveBrowsePosition();
      storage.setItem(KEY_PENDING, 'jump');
    },

    markRestoreAfterNavigation() {
      if (!storage) return;
      if (hasStoredPosition()) storage.setItem(KEY_PENDING, 'restore');
    },

    jumpAfterAjax() {
      this.saveBrowsePosition();
      requestAnimationFrame(() => requestAnimationFrame(scrollToResults));
    },

    restoreAfterAjax() {
      if (snapshotApplied) restoreBrowsePosition();
      else requestAnimationFrame(() => requestAnimationFrame(restoreBrowsePosition));
    },

    restoreBrowseGrid(container = document.getElementById('ProductGridContainer')) {
      if (!hasStoredPosition() || !container) return 0;
      const html = browseGrid ?? storage.getItem(KEY_GRID);
      if (html === null) return 0;
      container.innerHTML = html;
      snapshotApplied = true;
      return savedPage();
    },

    restoreImmediately() {
      if (storage?.getItem(KEY_BROWSE_URL)) return false;
      const page = this.restoreBrowseGrid();
      if (!page) return false;
      window.OCLoadMore?.onGridReplaced(page);
      restoreBrowsePosition(true);
      return true;
    },

    isRestoredImmediately() {
      return snapshotApplied;
    },

    reset() {
      if (!storage) return;
      clearSavedBrowse();
    },

    hasFilterParams(value = window.location.search) {
      const params = value instanceof URLSearchParams
        ? value
        : new URLSearchParams(String(value).replace(/^\?/, ''));
      return Array.from(params.keys()).some((key) => key.startsWith('filter.'));
    },

    applyPendingOnLoad() {
      if (!storage || storage.getItem(KEY_SCOPE) !== scope()) return;
      const pending = storage.getItem(KEY_PENDING);
      if (!pending) return;
      if (pending === 'restore') {
        const page = storage.getItem(KEY_BROWSE_URL) ? 0 : this.restoreBrowseGrid();
        if (page) window.OCLoadMore?.onGridReplaced(page);
        restoreBrowsePosition();
      } else requestAnimationFrame(() => requestAnimationFrame(() => {
        if (pending === 'jump') {
          scrollToResults();
          storage.removeItem(KEY_PENDING);
        }
      }));
    },
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => window.OCFilterScroll.applyPendingOnLoad(), { once: true });
  } else {
    window.OCFilterScroll.applyPendingOnLoad();
  }
})();
